// Google reviews slideshow. The markup is a native scroll-snap track that already works with
// touch, trackpad and keyboard; this adds previous/next, dots and an optional autoplay.
// Autoplay pauses on hover, focus, an off-screen section or a hidden tab, has a visible
// pause/play button (WCAG 2.2.2), and is never started for prefers-reduced-motion visitors.
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function setup(root) {
  if (root.dataset.tcReady) return;
  root.dataset.tcReady = 'true';
  const track = root.querySelector('[data-tc-reviews-track]');
  const slides = [...root.querySelectorAll('[data-tc-reviews-slide]')];
  const controls = root.querySelector('[data-tc-reviews-controls]');
  const dotsBox = root.querySelector('[data-tc-reviews-dots]');
  const toggle = root.querySelector('[data-tc-reviews-toggle]');
  const status = root.querySelector('[data-tc-reviews-status]');
  if (!track || slides.length < 2 || !controls) return;

  const interval = Math.max(4000, Number.parseInt(root.dataset.interval, 10) || 7000);
  let timer = 0;
  let userPaused = reduceMotion.matches || root.dataset.autoplay !== 'true';
  let hovering = false;
  let focused = false;
  let visible = true;

  const perView = () => {
    const first = slides[0].getBoundingClientRect().width;
    return first > 0 ? Math.max(1, Math.round(track.clientWidth / first)) : 1;
  };
  const lastStart = () => Math.max(0, slides.length - perView());
  const current = () => {
    const left = track.scrollLeft;
    let best = 0;
    slides.forEach((slide, index) => {
      if (Math.abs(slide.offsetLeft - track.offsetLeft - left) < Math.abs(slides[best].offsetLeft - track.offsetLeft - left)) best = index;
    });
    return Math.min(best, lastStart());
  };
  const goTo = (index, { announce = false } = {}) => {
    const target = Math.max(0, Math.min(index, lastStart()));
    track.scrollTo({ left: slides[target].offsetLeft - track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    if (announce && status) {
      const shown = perView();
      const end = Math.min(slides.length, target + shown);
      status.textContent = shown > 1 ? `Showing reviews ${target + 1} to ${end} of ${slides.length}` : `Review ${target + 1} of ${slides.length}`;
    }
  };

  let dots = [];
  const buildDots = () => {
    const count = lastStart() + 1;
    if (dots.length === count) return;
    dotsBox.replaceChildren();
    dots = Array.from({ length: count }, (_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tc-reviews__dot';
      dot.setAttribute('aria-label', `Go to review ${index + 1}`);
      dot.addEventListener('click', () => { goTo(index, { announce: true }); restart(); });
      dotsBox.append(dot);
      return dot;
    });
    controls.hidden = count < 2;
  };
  const markDots = () => {
    const active = current();
    dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
  };

  const running = () => !userPaused && !hovering && !focused && visible && document.visibilityState === 'visible';
  const tick = () => { const next = current() >= lastStart() ? 0 : current() + 1; goTo(next); };
  function restart() {
    window.clearInterval(timer);
    timer = running() ? window.setInterval(tick, interval) : 0;
  }
  const syncToggle = () => {
    if (!toggle) return;
    toggle.hidden = false;
    toggle.setAttribute('aria-pressed', String(userPaused));
    toggle.setAttribute('aria-label', userPaused ? 'Play the reviews slideshow' : 'Pause the reviews slideshow');
  };

  root.querySelector('[data-tc-reviews-prev]')?.addEventListener('click', () => {
    goTo(current() <= 0 ? lastStart() : current() - 1, { announce: true }); restart();
  });
  root.querySelector('[data-tc-reviews-next]')?.addEventListener('click', () => {
    goTo(current() >= lastStart() ? 0 : current() + 1, { announce: true }); restart();
  });
  toggle?.addEventListener('click', () => { userPaused = !userPaused; syncToggle(); restart(); });
  root.addEventListener('pointerenter', () => { hovering = true; restart(); });
  root.addEventListener('pointerleave', () => { hovering = false; restart(); });
  root.addEventListener('focusin', () => { focused = true; restart(); });
  root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) { focused = false; restart(); } });
  track.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') { event.preventDefault(); goTo(current() + 1, { announce: true }); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(current() - 1, { announce: true }); }
  });
  let scrollFrame = 0;
  track.addEventListener('scroll', () => { cancelAnimationFrame(scrollFrame); scrollFrame = requestAnimationFrame(markDots); }, { passive: true });
  document.addEventListener('visibilitychange', restart);
  reduceMotion.addEventListener?.('change', () => { if (reduceMotion.matches) { userPaused = true; syncToggle(); restart(); } });
  new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; restart(); }, { threshold: 0.25 }).observe(root);
  new ResizeObserver(() => { buildDots(); markDots(); }).observe(track);

  buildDots();
  markDots();
  if (root.dataset.autoplay === 'true') syncToggle();
  restart();
}

const init = () => document.querySelectorAll('[data-tc-reviews-carousel]').forEach(setup);
init();
document.addEventListener('shopify:section:load', init);
